import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Col,
  Row,
  Upload,
  Button,
  Modal,
  Spin,
  Tooltip,
  Drawer,
  Form,
  Input,
  Steps,
  DatePicker,
  Select,
  Space,
  Popconfirm,
  message,
  Segmented,
} from "antd";
import dayjs from "dayjs";
import {
  getOrdenesServicios,
  createOrdenServicio,
  updateOrdenServicio,
  deleteOrdenServicio,
} from "../../services/entregas/servicesOrdenesCompra";

import {
  getProveedores,
  createProveedor,
} from "../../services/entregas/servicesProveedoresPO";

import ProveedorForm from "./ProveedorForm";
import ProductosOrdenServicios from "./PorductosOrdenServicos";

import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../store/auth/selectors";

import Header166 from "../header/header";
import "./ordenesServicios.css";
import {
  UploadOutlined,
  FilePdfOutlined,
  SaveOutlined,
  PlusOutlined,
  CloseOutlined,
  ClearOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import OrdenesServiciosExcel from "./OrdenesServiciosExcel";

import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import LeerPO from "./LeerPO";

import config from "../../config";

const { Option } = Select;
const { Step } = Steps;
const { TextArea } = Input;

const { RangePicker } = DatePicker;
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const BASE_URL = config.baseUrl;

const OrdenesServicios = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentOrden, setCurrentOrden] = useState(null);

  const [form] = Form.useForm();

  const [submitting, setSubmitting] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [filterProveedor, setFilterProveedor] = useState(null);

  const [proveedoresData, setProveedoresData] = useState([]);

  const [modalProveedorVisible, setModalProveedorVisible] = useState(false);

  const [step, setStep] = useState(0);
  const [currentOidOrden, setCurrentOidOrden] = useState(null);

  const [hayCambiosSinGuardar, setHayCambiosSinGuardar] = useState(false);
  const [dateRange, setDateRange] = useState(null); // [dayjs('2025-01-01'), dayjs('2025-12-31')]

  const [mostrarLeerPO, setMostrarLeerPO] = useState(false);
  const [parsedProducts, setParsedProducts] = useState([]);

  //Control de Permisos
  const appName = "ordenesdeservicio";
  const permissions = useSelector((state) =>
    selectAppPermissions(state, appName)
  );
  const permissionDelete = permissions.priv_delete === "Y";
  const permissionUpdate = permissions.priv_update === "Y";
  const permissionInsert = permissions.priv_insert === "Y";
  const permissionExport = permissions.priv_export === "Y";
  const userBodecom = useSelector((state) => state.auth.user);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  //Fetch proveedores disponibles en el sistema
  const fetchProveedores = async () => {
    setLoading(true);
    setProveedoresData([]);

    try {
      const resp = await getProveedores();
      if (resp.status === 200 && Array.isArray(resp.data)) {
        setProveedoresData(resp.data);
      } else {
        setProveedoresData([]);
        message.error(resp.message || "No se pudieron cargar los proveedores");
      }
    } catch (error) {
      message.error("Error al cargar proveedores");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data ordenes de servicio
  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getOrdenesServicios();

      if (resp.status === 200 && Array.isArray(resp.data)) {
        setOrdenes(resp.data);
      } else {
        setOrdenes([]);
        message.error(resp.message || "No se pudieron cargar las órdenes");
      }
    } catch (error) {
      message.error("Error al cargar órdenes de servicio");
    } finally {
      setLoading(false);
    }
  };

  // Carga inicial
  useEffect(() => {
    fetchProveedores().then(fetchData); // Primero proveedores, luego órdenes
  }, []);

  // Filtered data
  const filtered = useMemo(() => {
    return ordenes.filter((o) => {
      const matchNum = o.numero_orden
        .toString()
        .toLowerCase()
        .includes(searchText.toLowerCase());
      const matchProv = filterProveedor
        ? o.proveedor_id === filterProveedor
        : true;

      const matchDate =
        !dateRange ||
        (!dateRange[0] && !dateRange[1]) ||
        (dayjs(o.fecha_po).isSameOrAfter(dateRange[0], "day") &&
          dayjs(o.fecha_po).isSameOrBefore(dateRange[1], "day"));

      return matchNum && matchProv && matchDate;
    });
  }, [ordenes, searchText, filterProveedor, dateRange]);

  // Open modal
  const openModal = (orden) => {
    setIsEditing(!!orden);
    setCurrentOrden(orden || null);

    setStep(0);
    if (orden) {
      form.setFieldsValue({
        oidProveedor: orden.proveedor_id,
        numero_orden: orden.numero_orden,
        fecha_po: dayjs(orden.fecha_po),
        observaciones: orden.observaciones || "",
        tipoOrden: orden.tipoOrden ?? 1,
        archivoPDF: [
          {
            uid: orden.oid ?? "-1",
            name: orden.ordenPDF.name,
            status: "done",
            originFileObj: orden.ordenPDF.url,
          },
        ],
      });
      setCurrentOidOrden(orden.oid);
    } else {
      form.resetFields();
      form.setFieldsValue({
        oidProveedor: null,
        numero_orden: null,
        fecha_po: null,
        observaciones: null,
      });
      setCurrentOidOrden(null);
      setCurrentOrden(null);
      setParsedProducts([]);
    }

    setModalVisible(true);
  };

  const handleSeleccionLeerPO = () => {
    setMostrarLeerPO(true);
    setModalVisible(false);
  };

  // Submit
  // Submit (crear o actualizar)
  const onFinish = async (values) => {
    // 1) Montamos el payload “básico” en un objeto plano
    const basicPayload = {
      oidProveedor: values.oidProveedor,
      numero_orden: values.numero_orden,
      fecha_po: values.fecha_po.format("YYYY-MM-DD"),
      observaciones: values.observaciones ?? "",
      userName: userBodecom.username,
      tipoOrden: Number(values.tipoOrden),
    };

    // 2) Comprobamos si, en modo edición, no hubo cambios
    if (isEditing && currentOrden) {
      const actualForCompare = {
        oidProveedor: basicPayload.oidProveedor,
        numero_orden: basicPayload.numero_orden,
        fecha_po: basicPayload.fecha_po,
        observaciones: basicPayload.observaciones,
        tipoOrden: Number(basicPayload.tipoOrden),
      };

      if (esIgualAlOriginal(currentOrden, actualForCompare)) {
        message.info("No se realizaron cambios en la Orden de Compra.");
        setStep(1);
        return;
      }
    }

    // 3) Sacamos el File real del array de Upload
    const fileList = values.archivoPDF; // <-- ahora es un array
    const pdfFile = Array.isArray(fileList) && fileList[0]?.originFileObj;

    // En creación obligamos a que exista
    if (!isEditing && !pdfFile) {
      message.error("Debes adjuntar el PDF de la Orden.");
      return;
    }

    let resp;
    try {
      setSubmitting(true);

      if (isEditing && currentOrden?.oid) {
        // 4a) Edición: si pusieron un PDF nuevo, enviamos FormData
        if (pdfFile) {
          const formData = new FormData();
          Object.entries(basicPayload).forEach(([k, v]) => {
            formData.append(k, v);
          });
          formData.append("archivoPDF", pdfFile);
          resp = await updateOrdenServicio(currentOrden.oid, formData);
        } else {
          // 4b) Edición sin cambiar PDF
          resp = await updateOrdenServicio(currentOrden.oid, basicPayload);
        }
      } else {
        // 5) Creación: FormData con el PDF obligatorio
        const formData = new FormData();
        Object.entries(basicPayload).forEach(([k, v]) => {
          formData.append(k, v);
        });
        formData.append("archivoPDF", pdfFile);
        resp = await createOrdenServicio(formData);
      }

      // 6) Manejo de la respuesta

      switch (resp.status) {
        case 201:
          message.success(resp.message || "Orden creada correctamente");
          setStep(1);
          setCurrentOidOrden(resp.data.insertedId);
          setCurrentOrden(basicPayload);
          fetchData();
          break;
        case 200:
          message.success(resp.message || "Orden actualizada correctamente");
          setStep(1);
          setCurrentOidOrden(currentOrden && currentOrden.oid);
          setCurrentOrden(basicPayload);
          fetchData();
          break;
        case 400:
          message.warning(
            resp.message || "Datos inválidos, por favor verifica."
          );
          break;
        case 409:
          message.error(resp.message || "Violación de restricción de datos");
          break;
        case 500:
          message.error(resp.message || "Error interno del servidor");
          break;
        default:
          message.info(resp.message || `Respuesta recibida: ${resp.status}`);
      }
    } catch (error) {
      message.error("Error inesperado al guardar la orden");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const esIgualAlOriginal = (original, actual) => {
    return (
      original.proveedor_id === actual.oidProveedor &&
      original.numero_orden === actual.numero_orden &&
      dayjs(original.fecha_po).format("YYYY-MM-DD") === actual.fecha_po &&
      (original.observaciones || "") === (actual.observaciones || "") &&
      original.tipoOrden === actual.tipoOrden
    );
  };

  // Columns
  const columns = [
    {
      title: "Acciones",
      key: "actions",
      align: "center",
      fixed: "left",
      render: (_, record) => (
        <div className="ordenes-servicios-filters-accions">
          {permissionUpdate && (
            <Tooltip title="Editar registro">
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => openModal(record)}
                title="Editar"
              />
            </Tooltip>
          )}

          {permissionDelete && (
            <Popconfirm
              title="¿Eliminar orden?, no podrá reversar está acción."
              onConfirm={() => handleDelete(record.oid)}
              okText="Sí"
              cancelText="No"
            >
              <Tooltip title="Eliminar registro">
                <Button
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                  title="Eliminar"
                />
              </Tooltip>
            </Popconfirm>
          )}

          {permissionExport && <OrdenesServiciosExcel oidOrden={record.oid} />}
        </div>
      ),
    },
    {
      title: "ID",
      dataIndex: "oid",
      key: "oid",
      align: "center",
      width: 50,
      sorter: (a, b) => a.oid - b.oid,
    },
    {
      title: "Número Orden",
      width: 150,
      align: "center",
      dataIndex: "numero_orden",
      key: "numero_orden",
    },
    {
      title: "Proveedor",
      dataIndex: "proveedor_nombre",
      key: "proveedor_nombre",
      width: 150,
    },
    {
      title: "Fecha PO",
      dataIndex: "fecha_po",
      key: "fecha_po",
      align: "center",
      width: 150,

      render: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Fecha Registro",
      dataIndex: "fechaRegistro",
      key: "fechaRegistro",
      align: "center",
      width: 150,

      render: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Valor Total (sin IVA)",
      dataIndex: "valortotal",
      key: "valortotal",
      width: 200,

      align: "center",
      sorter: (a, b) => (a.valortotal || 0) - (b.valortotal || 0),
      render: (value) =>
        value != null
          ? new Intl.NumberFormat("es-CO", {
              style: "currency",
              currency: "COP",
              minimumFractionDigits: 0,
            }).format(value)
          : "-",
    },

    {
      title: "Valor Total (con IVA)",
      dataIndex: "valortotal_con_iva",
      key: "valortotal_con_iva",
      width: 200,
      align: "center",
      sorter: (a, b) =>
        (a.valortotal_con_iva || 0) - (b.valortotal_con_iva || 0),
      render: (value) =>
        value != null
          ? new Intl.NumberFormat("es-CO", {
              style: "currency",
              currency: "COP",
              minimumFractionDigits: 2,
            }).format(value)
          : "-",
    },
    {
      title: "IVA",
      dataIndex: "total_iva",
      key: "total_iva",
      width: 200,
      align: "center",
      sorter: (a, b) => (a.total_iva || 0) - (b.total_iva || 0),
      render: (value) =>
        value != null
          ? new Intl.NumberFormat("es-CO", {
              style: "currency",
              currency: "COP",
              minimumFractionDigits: 2,
            }).format(value)
          : "-",
    },
    {
      title: "Observaciones",
      align: "center",
      dataIndex: "observaciones",
      key: "observaciones",
      width: 200,

      render: (text) => {
        const preview =
          text && text.length > 30 ? text.slice(0, 30) + "..." : text;
        return (
          <Tooltip title={text}>
            <span>{preview}</span>
          </Tooltip>
        );
      },
    },
  ];

  const handleDelete = async (oid) => {
    try {
      const resp = await deleteOrdenServicio(oid);

      switch (resp.status) {
        case 200:
          message.success(resp.message || "Orden eliminada correctamente");
          fetchData();
          break;

        case 400:
          message.warning(resp.message || "Petición inválida");
          break;

        case 404:
          message.error(resp.message || "Orden no encontrada");
          break;

        case 500:
          message.error(resp.message || "Error interno del servidor");
          break;

        default:
          message.info(resp.message || `Respuesta recibida: ${resp.status}`);
      }
    } catch (error) {
      message.error("Error inesperado al eliminar la orden");
      console.error(error);
    }
  };

  // limpiar filtros
  const clearFilters = () => {
    setSearchText("");
    setFilterProveedor(null);
    setDateRange(null); // <-- importante
  };

  const handleCrearProveedorDesdeOrden = async (values) => {
    try {
      const resp = await createProveedor(values);
      if (resp.status === 201) {
        message.success("Proveedor creado");
        setModalProveedorVisible(false);
        fetchProveedores();
        form.setFieldsValue({ oidProveedor: resp.data.oid }); // Asignar proveedor recién creado
      } else {
        message.error(resp.message || "No se pudo crear el proveedor");
      }
    } catch (err) {
      message.error("Error al crear proveedor");
      console.error(err);
    }
  };

  const handleCambiosGuardado = useCallback(
    (hayCambios, valorTotal) => {
      setHayCambiosSinGuardar(hayCambios);

      setOrdenes((prev) =>
        prev.map((orden) =>
          orden.oid === currentOidOrden
            ? { ...orden, valortotal: valorTotal }
            : orden
        )
      );
    },
    [currentOidOrden]
  );

  // 1️⃣ Handler que recibe el summary del hijo
  const handlePOIngresada = (summary, file) => {
    // summary = { ordenNumero, fecha, proveedor, totalPO, lineas }

    // 1. Cierra el modal de LeerPO
    setMostrarLeerPO(false);

    // 2. Abre el drawer de Nueva Orden
    openModal(null);

    // 3. Busca el proveedor en tu lista por nombre
    const prov = proveedoresData.find((p) => p.nombre === summary.proveedor);
    const oidProveedor = prov?.oid || null;

    // 4. Pre-llenar formulario
    form.setFieldsValue({
      oidProveedor,
      numero_orden: summary.ordenNumero,
      fecha_po: dayjs(summary.fecha),
      observaciones: "", // o algún otro campo que quieras
      // 5. Inyectamos el file en el campo archivoPDF
      archivoPDF: [
        {
          uid: file.uid ?? "-1",
          name: file.name,
          status: "done",
          originFileObj: file,
        },
      ],
    });

    setParsedProducts(summary.lineas);
  };

  const closeDrawer = () => {
    setModalVisible(false);
    setParsedProducts([]); // limpia la anterior carga
    setCurrentOidOrden(null);
    setCurrentOrden(null);
    setStep(0);
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const buildUrl = (u) => {
    if (!u) return null;
    return u.startsWith("http")
      ? u
      : `${BASE_URL || window.location.origin}/${u}`;
  };

  return (
    <div className="container">
      <Header166 type="ordens" />

      <div className="d-flex justify-content-between mb-3">
        <div className="ordenes-servicios-filters">
          <Input
            placeholder="Buscar número de orden"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200, marginRight: 8 }}
          />
          <Select
            showSearch
            placeholder="Filtrar proveedor"
            allowClear
            style={{ width: 300 }}
            onChange={(val) => setFilterProveedor(val)}
            value={filterProveedor}
            optionFilterProp="children"
            filterOption={(input, option) => {
              const prov = proveedoresData.find((p) => p.oid === option?.value);
              const nombre = prov?.nombre?.toLowerCase() || "";
              const identificacion = prov?.identificacion?.toLowerCase() || "";
              return (
                nombre.includes(input.toLowerCase()) ||
                identificacion.includes(input.toLowerCase())
              );
            }}
          >
            {proveedoresData.map((prov) => (
              <Option key={prov.oid} value={prov.oid}>
                {prov.nombre}
              </Option>
            ))}
          </Select>

          <RangePicker
            style={{ marginRight: 8 }}
            format="YYYY-MM-DD"
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            allowEmpty={[true, true]}
          />

          <Button
            icon={<ClearOutlined />}
            onClick={clearFilters}
            style={{ marginRight: 8 }}
          >
            Limpiar
          </Button>
        </div>

        <Space>
          {permissionInsert && (
            <>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openModal(null)}
              >
                Nueva PO
              </Button>

              <Button
                type="primary"
                icon={<UploadOutlined />}
                onClick={handleSeleccionLeerPO}
              >
                Leer PO
              </Button>
            </>
          )}

          {permissionExport && <OrdenesServiciosExcel ordenes={filtered} />}
        </Space>
      </div>

      <Table
        className="compact-table"
        dataSource={filtered}
        columns={columns}
        rowKey="oid"
        loading={loading}
        bordered
        size="small"
        scroll={{ y: 500, x: "max-content" }}
        pagination={{
          current: currentPage, // página actual
          pageSize: pageSize, // filas por página
          total: filtered.length, // total de registros
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} registros`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
      />

      <LeerPO
        modo="pdf"
        visible={mostrarLeerPO}
        onClose={() => setMostrarLeerPO(false)}
        onIngresar={(summary, file) => handlePOIngresada(summary, file)}
      />

      <Drawer
        placement="right"
        open={modalVisible}
        onClose={() => closeDrawer()}
        width="100vw"
        style={{ height: "100vh", padding: 0 }}
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              position: "relative",
            }}
          >
            <h6 style={{ margin: 0, fontWeight: "bold", paddingRight: 50 }}>
              {isEditing ? "Editar Orden de Compra" : "Nueva Orden de Compra"}
            </h6>
            <Button
              icon={<CloseOutlined />}
              onClick={() => setModalVisible(false)}
              style={{
                position: "absolute",
                right: 0,
                top: "50%",
                transform: "translateY(-50%)",
                border: "none",
                boxShadow: "none",
              }}
            />
          </div>
        }
      >
        <Steps
          current={Step}
          items={[
            {
              title: "Orden de Servicio",
            },
            {
              title: "Agregar Productos",
            },
          ]}
        />

        {step === 0 && (
          <>
            <Spin spinning={submitting} tip="Guardando...">
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                style={{ padding: 24 }}
              >
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="oidProveedor"
                      label="Proveedor"
                      rules={[
                        {
                          required: true,
                          message: "Seleccione o cree un proveedor",
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        placeholder="Seleccione un proveedor"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        dropdownRender={(menu) => (
                          <>
                            {menu}
                            <div style={{ padding: 8, textAlign: "center" }}>
                              <Button
                                type="link"
                                onClick={() => setModalProveedorVisible(true)}
                              >
                                + Nuevo proveedor
                              </Button>
                            </div>
                          </>
                        )}
                      >
                        {proveedoresData.map((prov) => (
                          <Option key={prov.oid} value={prov.oid}>
                            {prov.nombre}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Form.Item
                      name="numero_orden"
                      label="Número de Orden"
                      rules={[
                        {
                          required: true,
                          message: "Ingrese el número de orden",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={4}>
                    <Form.Item
                      name="fecha_po"
                      label="Fecha PO"
                      rules={[
                        { required: true, message: "Seleccione una fecha" },
                      ]}
                    >
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>

                  {/* 👉 Nuevo Col con el toggle */}
                  <Col span={6}>
                    <Form.Item
                      name="tipoOrden"
                      label="Tipo de Orden"
                      initialValue="1" // valor predeterminado
                      rules={[
                        {
                          required: true,
                          message: "Seleccione un tipo de orden",
                        },
                      ]}
                    >
                      <Segmented
                        className="segmented-orden"
                        options={[
                          { label: "PO", value: "1" },
                          { label: "Compra Local", value: "2" },
                        ]}
                        block // hace que ocupe todo el ancho
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="observaciones"
                      label="Observaciones de la Orden de compra"
                    >
                      <TextArea
                        rows={4}
                        placeholder="Ingrese observaciones (opcional)"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={24}>
                    {!isEditing ? (
                      <Form.Item
                        name="archivoPDF"
                        label="Adjuntar Orden (PDF)"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                        rules={
                          !isEditing
                            ? [
                                {
                                  required: true,
                                  message: "Debe cargar el documento de la PO",
                                },
                              ]
                            : []
                        }
                      >
                        <Upload
                          accept=".pdf"
                          beforeUpload={() => false}
                          maxCount={1}
                        >
                          <Button icon={<UploadOutlined />}>
                            Seleccionar PDF
                          </Button>
                        </Upload>
                      </Form.Item>
                    ) : (
                      <div>
                        <h3 style={{ marginBottom: 8 }}>
                          Documento orden de compra:{" "}
                          {currentOrden?.ordenPDF?.url ? (
                            <Button
                              type="primary"
                              danger
                              icon={<FilePdfOutlined />}
                              href={buildUrl(currentOrden.ordenPDF.url)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Abrir PDF
                            </Button>
                          ) : (
                            <span style={{ color: "#999" }}>
                              — sin PDF adjunto —
                            </span>
                          )}
                        </h3>
                      </div>
                    )}
                  </Col>
                </Row>

                <Form.Item>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: 16,
                      gap: 8,
                    }}
                  >
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SaveOutlined />}
                      loading={submitting}
                    >
                      Siguiente
                    </Button>
                  </div>
                </Form.Item>
              </Form>
            </Spin>

            {modalProveedorVisible && (
              <ProveedorForm
                visible={modalProveedorVisible}
                onCancel={() => setModalProveedorVisible(false)}
                onSubmit={handleCrearProveedorDesdeOrden}
                isEditing={false}
                form={form}
              />
            )}
          </>
        )}

        {step === 1 && (
          <>
            <ProductosOrdenServicios
              oidPo={currentOidOrden}
              isEditing={isEditing}
              onCambiosGuardado={handleCambiosGuardado}
              parsedProducts={parsedProducts}
              orden={currentOrden}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 16,
                gap: 8,
              }}
            >
              <Button
                type="primary"
                onClick={() => {
                  if (hayCambiosSinGuardar) {
                    Modal.confirm({
                      title: "Hay productos sin guardar",
                      content:
                        "¿Está seguro de finalizar? Algunos productos no han sido guardados y se perderán.",
                      okText: "Sí, finalizar",
                      cancelText: "Cancelar",
                      onOk: () => {
                        setStep(0);
                        setModalVisible(false);
                        setCurrentOidOrden(null);
                        setCurrentOrden(null);
                      },
                    });
                  } else {
                    setStep(0);
                    setModalVisible(false);
                    setCurrentOidOrden(null);
                    setCurrentOrden(null);
                  }
                }}
                disabled={loading} // opcional
              >
                Finalizar
              </Button>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default OrdenesServicios;
